import { Carousel } from "components/Elements/Carousel"
import { Markdown } from "components/Elements/Markdown"
import { useOperator } from "hooks"
import { useTranslation } from "react-i18next"

type HomeCarouselSlideProps = {
	title: string
	description: string
}

const SIGNUP_CAROUSEL_LENGTH = 2

const HomeCarouselSlide: React.FC<HomeCarouselSlideProps> = ({ title, description }) => {
	return (
		<section className="flex flex-col">
			<h2 className="c-h2 md:text-5xl text-3xl leading-tight mb-10">
				<Markdown>{title}</Markdown>
			</h2>
			<div className="md:text-2xl text-m lg:w-10/12 w-full">
				<Markdown
					specificComponents={{
						// Give more space to some elements
						p: ({ children }: any) => <p className="my-3">{children}</p>,
						li: ({ children }: any) => {
							return <li className="mt-1 pl-5">{children}</li>
						},
						a: ({ children, href }: Record<string, string>) => {
							return (
								<div className="flex items-start mt-5">
									<a
										className="c-btn c-btn--secondary-bordered"
										rel="noreferrer"
										href={href}
										target="_blank"
									>
										{children}
									</a>
								</div>
							)
						},
					}}
				>
					{description}
				</Markdown>
			</div>
		</section>
	)
}

type HomeCarouselProps = {
	initialSlideIndex?: number
	carouselSlidesLength?: number
	onSlideChange?: (...args: any[]) => void
}

export const HomeCarousel: React.FC<HomeCarouselProps> = ({
	initialSlideIndex,
	carouselSlidesLength = SIGNUP_CAROUSEL_LENGTH,
	onSlideChange,
}) => {
	const { t } = useTranslation()
	const { currentOperator } = useOperator()

	const slideContent: Array<HomeCarouselSlideProps> = Array.from(
		{ length: carouselSlidesLength },
		(_, i) => i + 1
	).map((slideIndex) => ({
		title: t(`carousel.slide_title_${slideIndex}`, {
			ns: `auth_${currentOperator.folderName}`,
		}),
		description: t(`carousel.slide_text_${slideIndex}`, {
			ns: `auth_${currentOperator.folderName}`,
		}),
	}))

	return (
		<Carousel
			onSlideChange={onSlideChange}
			defaultCurrentSlideIndex={initialSlideIndex}
			carouselSlidesLength={carouselSlidesLength}
		>
			{slideContent.map((slide, index) => (
				<HomeCarouselSlide {...slide} key={index} />
			))}
		</Carousel>
	)
}
